import { createFileRoute, Link } from "@tanstack/react-router";
import {
  EmployeeService,
  Material,
  MaterialService,
  WorkDay,
} from "../../api/generated";
import { useEffect, useState } from "react";
import { formatDate } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { MaterialScheduleForm } from "../../components/material_schedule_form";
import { ymdDash } from "../../components/search_bar";

export const Route = createFileRoute("/employee/$employeeId/schedule/")({
  loader: async ({ params }) => {
    const employeeId = Number(params.employeeId);
    const [employee, materials] = await Promise.all([
      EmployeeService.getEmployeeById(employeeId),
      MaterialService.getMaterials(),
    ]);

    return {
      employee,
      materials,
    };
  },
  component: RouteComponent,
});

type ScheduleState =
  | { status: "LOADING" }
  | { materials: Material[]; workSchedule: WorkDay; status: "READY" }
  | { error: Error; status: "ERROR" };

function emptyWorkSchedule(date: Date): WorkDay {
  return {
    date: formatDate(date, "yyyy-MM-dd"),
    materialRequirements: [],
  };
}

function RouteComponent() {
  const data = Route.useLoaderData();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [scheduleState, setScheduleState] = useState<ScheduleState>({
    status: "LOADING",
  });

  const changeDate = (days: number) => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + days);
      return newDate;
    });
  };

  const formattedDate = selectedDate.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  useEffect(() => {
    const fetchSchedule = async () => {
      setScheduleState({ status: "LOADING" });
      try {
        const schedule = await EmployeeService.getEmployeeSchedule(
          data.employee.id!,
          formatDate(selectedDate, ymdDash),
        );
        if (schedule) {
          setScheduleState({
            status: "READY",
            workSchedule: schedule,
            materials: data.materials,
          });
        } else {
          setScheduleState({
            status: "READY",
            workSchedule: emptyWorkSchedule(selectedDate),
            materials: data.materials,
          });
        }
      } catch (error) {
        setScheduleState({
          status: "ERROR",
          error: error as Error,
        });
      }
    };
    fetchSchedule();
  }, [selectedDate]);

  const onSave = async (schedule: WorkDay | null, error: Error | null) => {
    if (error) {
      setScheduleState({ status: "ERROR", error });
      return;
    }
    if (!schedule) {
      setScheduleState({
        status: "ERROR",
        error: new Error("No schedule data provided"),
      });
      return;
    }
    try {
      await EmployeeService.postEmployeeSchedule(data.employee.id!, schedule);
      setScheduleState({
        status: "READY",
        workSchedule: schedule,
        materials: data.materials,
      });
    } catch (error) {
      setScheduleState({
        status: "ERROR",
        error: error as Error,
      });
    }
  };

  return (
    <div className="flex justify-center items-center mt-10">
      <div className="flex w-3/4 flex-col items-center">
        <h1 className="text-2xl font-bold mb-4">
          <Link
            to="/employee/$employeeId"
            params={{ employeeId: String(data.employee.id!) }}
            className="link link-hover"
          >
            ← {data.employee.firstName} {data.employee.lastName}
          </Link>
        </h1>
        <div className="flex  w-full justify-between items-center mb-4">
          <button className="btn btn-secondary" onClick={() => changeDate(-1)}>
            ⬅ Vorige dag
          </button>
          <h2 className="text-lg font-semibold">{formattedDate}</h2>
          <button className="btn btn-secondary" onClick={() => changeDate(1)}>
            Volgende dag ➡
          </button>
        </div>

        {scheduleState.status === "LOADING" && (
          <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
        )}

        {scheduleState.status === "ERROR" && (
          <div role="alert" className="alert alert-error w-full">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 shrink-0 stroke-current"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>Kon geen rooster ophalen: {scheduleState.error.message}</span>
          </div>
        )}

        {scheduleState.status === "READY" && (
          <MaterialScheduleForm
            employee={data.employee}
            workSchedule={scheduleState.workSchedule}
            materials={scheduleState.materials}
            onSave={onSave}
          />
        )}
      </div>
    </div>
  );
}
